import { FC, useEffect, useState } from "react";
import { Draggable, DraggableStateSnapshot, DraggingStyle, NotDraggingStyle } from "react-beautiful-dnd";
import { DraggableIndicatorIcon, TrashIcon } from "../../../svgs";
import { Element, Mode, Type } from "../Form";
import Selector from "../../Selector/Selector";
import styles from './ElementCreator.module.scss';
import { Paragraph } from "../Paragraph/Paragraph";
import { ShortText } from "../ShortText/ShortText";
import { LongText } from "../LongText/LongText";
import { MultiSelection } from "../MultiSelection/MultiSelection";
import StyledCheckbox from "../../StyledCheckbox/StyledCheckbox";
import { SingleSelection } from "../SingleSelection/SingleSelection";
import { uniqueID } from "../../../../util";
import { useTranslation } from "react-i18next";

type Props = {
  element: Element,
  onElementChange: (element: Element | null) => void
  index: number,
}

export const ElementCreator: FC<Props> = ({ element, onElementChange, index }) => {
  const [loaded, setLoaded] = useState(false);
  const [t] = useTranslation('shared', { keyPrefix: 'form' });

  const LocalizedType = {
    [Type.ShortTextField]: t('element.shortTextField', 'Short text field'),
    [Type.LongTextField]: t('element.longTextField', 'Long text field'),
    [Type.ParagraphElement]: t('element.paragraph', 'Paragraph'),
    [Type.MultiSelectionElement]: t('element.multipleSelection', 'Multiple selection'),
    [Type.SingleSelectionElement]: t('element.singleSelection', 'Single selection'),
  }

  const getStyles = (
    style: DraggingStyle | NotDraggingStyle | undefined,
    snapshot: DraggableStateSnapshot
  ) => {
    const styles: (DraggingStyle | NotDraggingStyle | undefined) & { transform?: string, backgroundColor?: string, border?: string } = { ...style };
    if (styles?.transform) {
      const axisLockY =
        "translate(0px" +
        styles.transform.slice(
          styles.transform.indexOf(","),
          styles.transform.length
        );
      styles.transform = axisLockY;
    }

    if (snapshot.isDragging) {
      styles.backgroundColor = 'var(--gray-50)';
      styles.border = '1px solid var(--gray-300)';
    } else {
      styles.backgroundColor = 'white';
    }

    return styles;
  }

  const createElementOfTypeWithOptions = (type: Type, element: Element) => {
    if (type === Type.ParagraphElement) {
      type elementType = ShortText | LongText | MultiSelection | SingleSelection;
      onElementChange(new Paragraph({ renderId: element.renderId, text: (element as elementType).question ?? '' }));
    } else if (type === Type.ShortTextField) {
      type elementType =  LongText | MultiSelection | SingleSelection;
      const question = element instanceof Paragraph ? element.text : (element as elementType).question;
      onElementChange(new ShortText({ renderId: element.renderId, question:question ?? '', required: (element as elementType).required }));
    } else if (type === Type.LongTextField) {
      type elementType =  ShortText | MultiSelection | SingleSelection;
      const question = element instanceof Paragraph ? element.text : (element as elementType).question;
      onElementChange(new LongText({ renderId: element.renderId, question:question ?? '', required: (element as elementType).required }));
    } else if (type === Type.MultiSelectionElement) {
      type elementType =  ShortText | LongText | SingleSelection;
      const question = element instanceof Paragraph ? element.text : (element as elementType).question;
      const options = element instanceof SingleSelection ? element.options : [{label: '', value: uniqueID()+'', selected: false}];
      onElementChange(new MultiSelection({ renderId: element.renderId, question: question ?? '', options, required: (element as elementType).required, }));
    } else if (type === Type.SingleSelectionElement) {
      type elementType =  ShortText | LongText | MultiSelection;
      const question = element instanceof Paragraph ? element.text : (element as elementType).question;
      const options = element instanceof MultiSelection ? element.options : [{label: '', value: uniqueID()+'', selected: false}];
      onElementChange(new SingleSelection({ renderId: element.renderId, question: question ?? '', options, required: (element as elementType).required, }));
    }
  }

  useEffect(() => {
    setLoaded(true);
  }, []);

  // Every time we drag and drop this, the Editor has to rerender.
  useEffect(() => {
    if(!loaded) return;
    const oldRenderId = element.renderId;
    setTimeout(() => {
      element.renderId = `${oldRenderId}-${index}`;
      onElementChange(element);
    }, 100);
    setTimeout(() => {
      element.renderId = oldRenderId;
      onElementChange(element);
    }, 200);
  }, [index]);

  return (
    <Draggable key={element.renderId} draggableId={element.renderId} index={index}>
      {(_provided, snapshot) => (
        <li ref={_provided.innerRef} {..._provided.draggableProps} style={getStyles(_provided.draggableProps.style, snapshot)} className={styles.elementCreator}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: '32px' }}>
              <div {..._provided.dragHandleProps}><DraggableIndicatorIcon /></div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Selector
                    isClearable={false}
                    isSearchable={false}
                    options={Object.values(Type).map(type => ({ label: LocalizedType[type], value: type }))}
                    value={{ label: LocalizedType[element.type], value: element.type }}
                    onChange={(value) => createElementOfTypeWithOptions(value?.value as Type, element)}
                    className={styles.typeSelector}
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        backgroundColor: 'transparent !important',
                      }),
                    }}
                  />
                  {
                    element instanceof Paragraph ? null : (
                      <label style={{ letterSpacing: 'normal', color: 'var(--gray-700)', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <StyledCheckbox
                          onChange={() => {
                            element.required = !element.required;
                            onElementChange(element);
                          }}
                          checked={element.required}
                        />
                        <span>{t('elementCreator.required', 'Required')}</span>
                      </label>
                    )
                  }
                </div>
                <div className={styles.deleteButton} onClick={() => {
                  onElementChange(null);
                }}>
                  <TrashIcon size='14px' color='var(--gray-700)' />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {element.render(Mode.EDIT, onElementChange)}
              </div>
            </div>
          </div>
        </li>
      )}
    </Draggable>
  )
}